import React from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { useState } from 'react';
import { ImageList, ImageListItem, CircularProgress, IconButton } from '@mui/material';
import { Button, Snackbar, Alert } from '@mui/material';
import { API_BASE, authHeaders, publicFileUrl } from '../../../../api';

const validationSchema = yup.object({
  title: yup.string().trim().required('Please specify your title'),
});

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const Contact = () => {
  const [itemData, setItemData] = useState([]);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState({});
  const [open, setOpen] = useState(false);

  const handleClick = () => {
    setOpen(true);
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const theme = useTheme();

  const uploadFile = async ({ file }) => {
    if (!file) {
      throw new Error('File is required');
    }

    const body = new FormData();
    body.append('file', file);

    const res = await fetch(`${API_BASE}/api/upload`, {
      method: 'POST',
      headers: { ...authHeaders() },
      body,
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.message || 'Upload failed');
    }

    const path = data.url || data.Location;
    return {
      path,
      publicUrl: publicFileUrl(path),
    };
  };

  const handleFileChange = async (event) => {
    const files = event.target.files;

    setError('');

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileId = `${Date.now()}-${i}`;

      if (!file.type.startsWith('image/')) {
        setError('Only image files are allowed.');
        continue;
      }
      if (file.size > 5000000) {
        setError('File size must be less than 5MB.');
        continue;
      }

      setUploading(prev => ({
        ...prev,
        [fileId]: { progress: 0, fileName: file.name }
      }));

      try {
        const reader = new FileReader();
        reader.onloadend = () => {
          setItemData(prev => [...prev, {
            id: fileId,
            img: reader.result,
            title: file.name,
            uploading: true
          }]);
        };
        reader.readAsDataURL(file);

        const { path, publicUrl } = await uploadFile({ file });

        setItemData(prev => prev.map(item =>
          item.id === fileId
            ? {
              ...item,
              uploading: false,
              url: publicUrl,
              key: path,
            }
            : item
        ));

        setUploading(prev => {
          const newState = { ...prev };
          delete newState[fileId];
          return newState;
        });

      } catch (err) {
        setError(`Failed to upload ${file.name}: ${err.message}`);
        console.error('Upload error:', err);

        setItemData(prev => prev.filter(item => item.id !== fileId));
        setUploading(prev => {
          const newState = { ...prev };
          delete newState[fileId];
          return newState;
        });
      }
    }
  };

  const handleDeleteImage = (id) => {
    setItemData(prev => prev.filter(item => item.id !== id));
  };

  const initialValues = {
    title: '',
  };

  const onSubmit = async (values) => {
    if (itemData.length === 0) {
      setError('Please upload at least one image before submitting');
      return;
    }

    try {
      const formData = {
        title: values.title,
        images: itemData.map(item => item.url).filter(Boolean),
      };

      const res = await fetch(`${API_BASE}/api/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders(),
        },
        body: JSON.stringify(formData),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || 'Submission failed. Please try again.');
        return;
      }

      formik.resetForm();
      setItemData([]);
      setError('');
      handleClick();
    } catch (err) {
      setError('Error submitting form. Please try again.');
      console.error('Submit error:', err);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  return (
    <Box>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6} data-aos={'fade-up'}>
          <Box maxWidth={400} margin={'0 auto'}>
            <Card sx={{ backgroundImage: 'none', boxShadow: 4 }}>
              <CardMedia
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  component={'svg'}
                  preserveAspectRatio="none"
                  xmlns="http://www.w3.org/2000/svg"
                  x="0px"
                  y="0px"
                  viewBox="0 0 1920 100.1"
                  sx={{
                    width: '100%',
                    bottom: 0,
                    position: 'absolute',
                  }}
                >
                  <path
                    fill={theme.palette.background.default}
                    d="M0,0c0,0,934.4,93.4,1920,0v100.1H0L0,0z"
                  ></path>
                </Box>
              </CardMedia>
              <CardContent>
                <form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="Title"
                        name="title"
                        fullWidth
                        value={formik.values.title}
                        onChange={formik.handleChange}
                        error={formik.touched.title && Boolean(formik.errors.title)}
                        helperText={formik.touched.title && formik.errors.title}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        component="label"
                        variant="contained"
                        fullWidth
                        startIcon={<CloudUploadIcon />}
                        disabled={Object.keys(uploading).length > 0}
                      >
                        Upload files
                        <VisuallyHiddenInput
                          type="file"
                          onChange={handleFileChange}
                          accept="image/*"
                          multiple
                          disabled={Object.keys(uploading).length > 0}
                        />
                      </Button>
                    </Grid>
                    {error && (
                      <Grid item xs={12}>
                        <Box sx={{ color: 'error.main', mt: 1 }}>
                          {error}
                        </Box>
                      </Grid>
                    )}
                    {Object.keys(uploading).map(fileId => (
                      <Grid item xs={12} key={fileId}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <CircularProgress size={20} />
                          <Box sx={{ flexGrow: 1 }}>
                            {`Uploading ${uploading[fileId].fileName}...`}
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        fullWidth
                        type="submit"
                        disabled={Object.keys(uploading).length > 0}
                      >
                        Submit
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </Box>
        </Grid>
        <Grid
          item
          container
          alignItems={'center'}
          xs={12}
          md={6}
          data-aos={'fade-up'}
        >
          <Box>
            <ImageList sx={{ width: 500, height: 450 }} cols={3} rowHeight={164}>
              {itemData.map((item) => (
                <ImageListItem key={item.id} sx={{ position: 'relative' }}>
                  <img
                    src={item.img}
                    alt={item.title}
                    loading="lazy"
                  />
                  {item.uploading && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  )}
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: 5,
                      right: 5,
                      bgcolor: 'background.paper',
                      '&:hover': {
                        bgcolor: 'background.paper',
                      },
                    }}
                    size="small"
                    onClick={() => handleDeleteImage(item.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ImageListItem>
              ))}
            </ImageList>
          </Box>
          <Snackbar
            open={open}
            autoHideDuration={3000}
            onClose={handleClose}
          >
            <Alert onClose={handleClose} severity="success" sx={{ width: '100%' }}>
              Upload Successful!
            </Alert>
          </Snackbar>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Contact;
